/**
 * 工单管理组件，包含：
 * 1、工单导入
 */

import { job_order } from '@/interfaces/job_order'
import { Timestamp } from '@/interfaces/Timestamp'
import { jobOrderRequest } from '@/network/requests/jobOrder'
import { jtInfoRequest } from '@/network/requests/jtInfo'
import { processGroupRequest } from '@/network/requests/processGroup'
import { getProcessGroupDetails } from '@/network/requests/processGroupDetail'
import { productRequest } from '@/network/requests/product'
import { AxiosResponse } from 'axios'
import moment from 'moment'
import XLSX from 'xlsx'
import { getFactoryUsers } from '@/network/api/user'
import { getDBName } from '@/network/config'

/**
 * 产品导入校验对象。
 */
interface JobOrderImportValidate {
	/**
	 *
	 */
	[propName: string]: string
}

/**
 * 工单导入对象。
 */
interface JobOrderImportObject {
	/**
	 * 编号。
	 */
	code: string
	/**
	 * 工单id
	 */
	work_order_id?: string
	/**
	 * 工单编号，人工输入字符编号
	 */
	work_order_code?: string
	/**
	 * 机台号
	 */
	jt_id?: string
	/**
	 * 机台名称。
	 */
	jt_name?: string
	/**
	 * 产品Id。
	 */
	product_id?: string
	/**
	 * 产品名称。
	 */
	product_name?: string
	/**
	 * 工人Id。
	 */
	worker_id?: string
	/**
	 * 工人账号。
	 */
	worker_name: string
	/**
	 * 计划时间，时间戳。
	 */
	plan_time?: string
	/**
	 * 开始时间，时间戳
	 */
	start_time?: string
	/**
	 * 计划开始时间
	 */
	plan_begin_time?: string
	/**
	 * 计划结束时间
	 */
	plan_end_time?: string
	/**
	 * 开机时间。
	 */
	begin_time?: string
	/**
	 * 结束时间，时间戳
	 */
	end_time?: string
	/**
	 * 托盘数
	 */
	plates: number
	/**
	 * 准备时间，单位分钟
	 */
	pre_time: number
	/**
	 * 状态，0空，1准备，2印刷，3复合，4分切，5复卷
	 */
	status: number
	/**
	 * 总长度
	 */
	total_len: number
	/**
	 * 每卷长度
	 */
	roll_len: number
	/**
	 * 卷数量
	 */
	roll_num: number
	/**
	 * 工序号的显示值，从1开始。
	 */
	process_index: number
	/**
	 * 工序号的索引值，从0开始
	 */
	process_index_value: number
	/**
	 *
	 */
	message: string
	/**
	 * 校验结果。
	 */
	validateInfo?: JobOrderImportValidate
	/**
	 * 是否出错。
	 */
	hasError: boolean
	/**
	 * 是否合格。
	 */
	isValid: boolean
	/**
	 * 保存结果。
	 */
	saveResult?: string
	/**
	 * 是否保存成功。
	 */
	saved?: boolean
}

export class JobOrderImport {
	/**
	 * 文件对象。
	 */
	inputFile: any

	/**
	 * 产品导入数据结果。
	 */
	dataTable: JobOrderImportObject[] = []

	/**
	 * 文件解析完成后的回调函数。
	 */
	analysisCallback: Function

	/**
	 * 工厂用户。
	 */
	workerUsers: any[] = []

	/**
	 * 通过选择的Excel文件对象进行组件构造。
	 * @param binarryFileString 文件对象。
	 * @param analysisCallback  文件分析完成后的回调函数。
	 */
	constructor(binarryFileString: any, analysisCallback: Function) {
		this.inputFile = binarryFileString
		this.analysisCallback = analysisCallback

		var reader = new FileReader()
		reader.onload = e => this.readerOnload(e, this)
		reader.readAsBinaryString(binarryFileString)
	}

	/**
	 * 进行文件读取。
	 * @param e
	 * @param self
	 */
	private readerOnload(e: ProgressEvent<FileReader>, self: this) {
		var data = e.target?.result
		var wb = XLSX.read(data, {
			type: 'binary',
		})

		var sheet0 = wb.Sheets[wb.SheetNames[0]]

		//使用raw属性，避免日期格式进行转换。
		var array: any[] = XLSX.utils.sheet_to_json(sheet0, { raw: false })
		var list: JobOrderImportObject[] = []

		console.log(array)
		for (var item of array) {
			//工序索引单独处理，因为从用户的角度理解索引是从1开始的，但是程序当中是从0开始的；因此在这里-1；
			var value = parseInt(item['工序索引'])

			list.push({
				isValid: false,
				work_order_id: item['工单ID'],
				work_order_code: item['工单号'],
				code: item['作业号'],
				jt_name: item['机台名称'],
				product_name: item['产品名称'],
				worker_name: item['用户名'],
				plan_begin_time: item['计划开始时间'],
				plan_end_time: item['计划结束时间'],
				plates: item['托盘数'],
				pre_time: item['准备时间'],
				status: item['状态'],
				total_len: item['总长度'],
				roll_num: item['总卷数'],
				roll_len: item['每卷长度'],
				process_index: value,
				process_index_value: value > 0 ? value - 1 : 0,
				message: '准备导入',
				hasError: false,
				validateInfo: {},
			})
		}

		//执行回调。
		self.analysisCallback(list)

		self.dataTable = list
	}

	/**
	 * 删除一条记录。
	 * @param index
	 */
	deleteRow(index: number): void {
		this.dataTable.splice(index, 1)
	}

	/**
	 * 保存数据。
	 */
	save(): Promise<AxiosResponse<any>> {
		let hasError = this.dataTable.filter(t => t.hasError)

		console.log(this.dataTable)

		if (hasError.length > 0) {
			return Promise.reject('')
		}

		var jobOrders: job_order[] = []
		//遍历数据，换换成产品对象。
		for (let i = 0; i < this.dataTable.length; i++) {
			const t = this.dataTable[i]

			var p = this.getJobOrder(t)
			jobOrders.push(p)
		}

		//上传工单记录。
		return jobOrderRequest.uploadBatch(jobOrders)

		//记录日志。
	}

	/**
	 * 进行数据校验。
	 */
	async checkData(): Promise<JobOrderImportObject[]> {
		//遍历数据。
		var result: JobOrderImportObject[] = []
		for (const item of this.dataTable) {
			var t = await this.validate(item)

			result.push(t)
		}

		this.dataTable = result

		return result
	}

	/**
	 * 进行数据校验。
	 * @param p
	 */
	async validate(p: JobOrderImportObject): Promise<JobOrderImportObject> {
		console.log('进行数据校验')

		p.message = '正在进行名称检查...'
		p.validateInfo = {}
		p.isValid = false

		let p1 = jobOrderRequest.queryAll({ code: p.code }) //作业号检查。
		let p2 = productRequest.queryAll({ name: p.product_name }) //查询产品名称。
		let p3 = jtInfoRequest.queryAll({ name: p.jt_name }) //查询机台。

		//let p4 = processGroupRequest.queryAll({ name: p.processGroupName })

		var results = await Promise.all([p1, p2, p3])

		//检查作业号重名。
		var data = results[0]
		if (!p.code) {
			p.validateInfo['code'] = '作业号无效。'
			p.hasError = true
		} else {
			if (data.length > 0 || this.dataTable.filter(t => t.code == p.code).length > 1) {
				p.validateInfo['code'] = '已存在'
				p.hasError = true
			}
		}

		//检查工单ID。
		if (!p.work_order_id) {
			p.validateInfo['work_order_id'] = '工单ID无效。'
			p.hasError = true
		}

		//检查工单号。
		if (!p.work_order_code) {
			p.validateInfo['work_order_code'] = '工单号无效'
			p.hasError = true
		}

		//检查产品名称。
		var products = results[1]
		if (products.length > 0) {
			if (products.length > 1) {
				p.validateInfo['product_name'] = `产品名称数据错误，存在多个相同名称的产品：${p.product_name},请检查基础数据的配置。`
				p.hasError = true
			} else {
				p.product_id = products[0]._id?.$oid
			}
		} else {
			p.validateInfo['product_name'] = `产品名称无效`
			p.hasError = true
		}

		//检查机台。
		var jts = results[2]
		if (jts.length > 0) {
			if (jts.length > 1) {
				p.validateInfo['jt_name'] = `机台数据错误，存在多个相同名称的机台：${p.jt_name},请检查基础数据的配置。`
				p.hasError = true
			} else {
				p.jt_id = jts[0]._id?.$oid
			}
		} else {
			p.validateInfo['jt_name'] = `机台不存在`
			p.hasError = true
		}

		if (this.workerUsers.length == 0) {
			//获取所有工厂用户。
			var dbName = getDBName()
			var factoryUsersResponse = await getFactoryUsers(dbName)
			if (factoryUsersResponse.success) {
				factoryUsersResponse.result.forEach(p => {
					this.workerUsers.push({ ...p })
				})

				console.log('workers', this.workerUsers)
			}
		}

		//检查用户名，找到就赋值。
		if (p.worker_name) {
			var user = this.workerUsers.find(t => t.username == p.worker_name)
			if (user == null) {
				p.validateInfo['worker_name'] = '用户名不存在'
				p.hasError = true
			} else {
				p.worker_id = user.id
			}
		} else {
			p.validateInfo['worker_name'] = `不允许为空`
			p.hasError = true
		}

		//计划开始时间
		if (p.plan_begin_time) {
			var t = moment(p.plan_begin_time).valueOf()
			if (isNaN(t)) {
				p.validateInfo['plan_begin_time'] = `日期格式错误`
				p.hasError = true
			}
		} else {
			p.validateInfo['plan_begin_time'] = `不允许为空`
			p.hasError = true
		}

		//计划结束时间
		if (p.plan_end_time) {
			var t = moment(p.plan_end_time).valueOf()
			if (isNaN(t)) {
				p.validateInfo['plan_end_time'] = `日期格式错误`
				p.hasError = true
			}
		} else {
			p.validateInfo['plan_end_time'] = `不允许为空`
			p.hasError = true
		}

		//托盘数量
		if (isNaN(p.plates)) {
			p.validateInfo['plates'] = '格式错误'
			p.hasError = true
		} else {
			if (p.plates < 0) {
				p.plates = 0
			} else if (p.plates > 99999) {
				p.plates = 99999
			}
		}

		//准备时间
		if (isNaN(p.pre_time)) {
			p.validateInfo['pre_time'] = '不允许为空'
			p.hasError = true
		} else {
			if (p.pre_time < 0) {
				p.pre_time = 0
			} else if (p.pre_time > 99999) {
				p.plates = 99999
			}
		}

		//工单状态检查。
		if (isNaN(p.status)) {
			p.validateInfo['status'] = '格式错误'
			p.hasError = true
		} else {
			if (p.status < 0) {
				p.status = 0
			} else if (p.status > 5) {
				p.status = 5
			}
		}

		//总长度
		if (isNaN(p.total_len)) {
			p.validateInfo['total_len'] = '格式错误'
			p.hasError = true
		} else {
			if (p.total_len < 0) {
				p.total_len = 0
			} else if (p.total_len > 9999999) {
				p.total_len = 9999999
			}
		}

		//每卷长度
		if (isNaN(p.roll_len)) {
			p.validateInfo['roll_len'] = '格式错误'
			p.hasError = true
		} else {
			if (p.roll_len < 0) {
				p.roll_len = 0
			} else if (p.roll_len > 9999999) {
				p.roll_len = 9999999
			}
		}

		//总卷数
		if (isNaN(p.roll_num)) {
			p.validateInfo['roll_num'] = '格式错误'
			p.hasError = true
		} else {
			if (p.roll_num < 0) {
				p.roll_num = 0
			} else if (p.roll_num > 9999999) {
				p.roll_num = 9999999
			}
		}

		//检查工序索引值。
		if (isNaN(p.process_index)) {
			p.validateInfo['process_index'] = '格式错误'
			p.hasError = true
		} else {
			if (p.process_index < 0) {
				p.process_index = 0
			} else if (p.process_index > 9999999) {
				p.process_index = 9999999
			}

			if (products.length == 1) {
				var product = products[0]
				if (product.process_group_id) {
					//根据工序组ID查询工序组对象。
					var processGroupInfo = await processGroupRequest.getDocumentById(product.process_group_id)
					//再查询工序详情。
					var details = await getProcessGroupDetails(processGroupInfo.data._id.$oid)
					if (p.process_index > -1 && p.process_index < details.length) {
						var process = details[p.process_index]
					} else {
						p.validateInfo['process_index'] = '工序组索引无效，超出范围'
						p.hasError = true
					}
				} else {
					p.validateInfo['process_index'] = '工序组索引无法匹配，产品未配置工序'
					p.hasError = true
				}
			} else {
				p.validateInfo['process_index'] = '工序组无法匹配，产品不存在'
				p.hasError = true
			}
		}

		if (p.hasError) {
			p.message = '校验失败，请检查数据。'
		}

		console.log('校验结束', p)

		if (!p.hasError) {
			p.message = '校验通过'
			p.isValid = true
		} else {
			p.isValid = false
		}

		return p
	}

	/**
	 * 进行数据对象转换。
	 * @param p
	 */
	private getJobOrder(p: JobOrderImportObject): job_order {
		var plan_begin_time = moment(p.plan_begin_time).valueOf()
		var plan_end_time = moment(p.plan_end_time).valueOf()

		return {
			work_order_id: p.work_order_id,
			work_order_code: p.work_order_code,
			code: p.code,
			jt_id: p.jt_id,
			product_id: p.product_id,
			worker_id: p.worker_id,
			plan_time: undefined,
			start_time: undefined,
			plan_begin_time: {
				$date: plan_begin_time,
			},
			plan_end_time: {
				$date: plan_end_time,
			},
			begin_time: undefined,
			end_time: undefined,
			plates: p.plates,
			pre_time: p.pre_time,
			status: p.status,
			total_len: p.total_len,
			roll_len: p.roll_len,
			roll_num: p.roll_num,
			process_group_id: '',
			process_index: p.process_index_value,
			uploadFinished: false,
		}
	}
}
